import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import styles from './AddUnitToCustomer.module.css';
import { searchProjects } from '../services/projectService';
import { searchUnits } from '../services/unitService';
import { fetchCustomerDetails } from '../services/customerService';
import { showErrorNotification } from '../store/slices/notificationSlice';

const AddUnitToCustomer = ({ customerId: propCustomerId, onCancel }) => {
    const { customerId: urlCustomerId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Use prop customerId if provided, otherwise fall back to URL parameter
    const customerId = propCustomerId || urlCustomerId;

    // Get customer data from URL parameters (passed from AddCustomer success dialog)
    const customerName = searchParams.get('customerName');
    const customerEmail = searchParams.get('customerEmail');
    const customerSwagId = searchParams.get('customerSwagId');

    // Customer data - use URL params if available, otherwise fetch from API
    const [customer, setCustomer] = useState(() => {
        if (customerName && customerEmail) {
            return {
                _id: customerId,
                name: customerName,
                email: customerEmail,
                swag_id: customerSwagId
            };
        }
        return null;
    });

    const [customerLoading, setCustomerLoading] = useState(!customer);

    // Project search
    const [projectSearchQuery, setProjectSearchQuery] = useState('');
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [projectsLoading, setProjectsLoading] = useState(false);
    const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
    const projectDropdownRef = useRef(null);

    // Unit search
    const [unitSearchQuery, setUnitSearchQuery] = useState('');
    const [units, setUnits] = useState([]);
    const [selectedUnit, setSelectedUnit] = useState(null);
    const [unitsLoading, setUnitsLoading] = useState(false);
    const [isUnitDropdownOpen, setIsUnitDropdownOpen] = useState(false);
    const unitDropdownRef = useRef(null);

    // Debounce timeouts
    const [projectSearchTimeout, setProjectSearchTimeout] = useState(null);
    const [unitSearchTimeout, setUnitSearchTimeout] = useState(null);

    // Fetch customer details only if not available from URL parameters
    useEffect(() => {
        const fetchCustomer = async () => {
            if (customerId && !customer) {
                try {
                    const response = await fetchCustomerDetails(customerId);
                    const fetchedCustomer = response.data || response;
                    // Update the customer state with fetched data
                    setCustomer({
                        _id: customerId,
                        name: fetchedCustomer.name,
                        email: fetchedCustomer.email,
                        address: fetchedCustomer.address,
                        swag_id: fetchedCustomer.swag_id
                    });
                } catch (error) {
                    console.error('Failed to fetch customer details:', error);
                    dispatch(showErrorNotification(`Failed to load customer details: ${error.message}`));
                } finally {
                    setCustomerLoading(false);
                }
            } else if (customer) {
                // Customer data is already available from URL params
                setCustomerLoading(false);
            }
        };

        fetchCustomer();
    }, [customerId, dispatch]);

    // Search projects when search query changes
    useEffect(() => {
        // Clear any existing timeout
        if (projectSearchTimeout) {
            clearTimeout(projectSearchTimeout);
        }

        const searchProjectsAPI = async () => {
            if (projectSearchQuery.trim().length > 0) {
                setProjectsLoading(true);
                try {
                    const response = await searchProjects(projectSearchQuery);
                    if (response.data && Array.isArray(response.data)) {
                        setProjects(response.data);
                    } else {
                        setProjects([]);
                    }
                } catch (error) {
                    console.error('Failed to search projects:', error);
                    setProjects([]);
                    dispatch(showErrorNotification(`Failed to search projects: ${error.message}`));
                } finally {
                    setProjectsLoading(false);
                }
            } else {
                setProjects([]);
                setProjectsLoading(false);
            }
        };

        // Debounce the search to avoid too many API calls
        const timeoutId = setTimeout(searchProjectsAPI, 500);
        setProjectSearchTimeout(timeoutId);

        return () => {
            clearTimeout(timeoutId);
            if (projectSearchTimeout) {
                clearTimeout(projectSearchTimeout);
            }
        };
    }, [projectSearchQuery, dispatch]);

    // Search units when unit search query changes and project is selected
    useEffect(() => {
        // Clear any existing timeout
        if (unitSearchTimeout) {
            clearTimeout(unitSearchTimeout);
        }

        const searchUnitsAPI = async () => {
            if (selectedProject && unitSearchQuery.trim().length > 0) {
                setUnitsLoading(true);
                try {
                    const response = await searchUnits(selectedProject._id, unitSearchQuery);
                    if (response.data && response.data.data && Array.isArray(response.data.data)) {
                        setUnits(response.data.data);
                    } else {
                        setUnits([]);
                    }
                } catch (error) {
                    console.error('Failed to search units:', error);
                    setUnits([]);
                    dispatch(showErrorNotification(`Failed to search units: ${error.message}`));
                } finally {
                    setUnitsLoading(false);
                }
            } else {
                setUnits([]);
                setUnitsLoading(false);
            }
        };

        // Debounce the search to avoid too many API calls
        const timeoutId = setTimeout(searchUnitsAPI, 500);
        setUnitSearchTimeout(timeoutId);

        return () => {
            clearTimeout(timeoutId);
            if (unitSearchTimeout) {
                clearTimeout(unitSearchTimeout);
            }
        };
    }, [unitSearchQuery, selectedProject, dispatch]);

    // Handle click outside dropdowns
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (projectDropdownRef.current && !projectDropdownRef.current.contains(event.target)) {
                setIsProjectDropdownOpen(false);
            }
            if (unitDropdownRef.current && !unitDropdownRef.current.contains(event.target)) {
                setIsUnitDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleProjectSelect = (project) => {
        setSelectedProject(project);
        setIsProjectDropdownOpen(false);
        setProjectSearchQuery('');
        setProjects([]);
        // Reset unit selection when project changes
        setSelectedUnit(null);
        setUnitSearchQuery('');
        setUnits([]);
    };

    const handleUnitSelect = (unit) => {
        setSelectedUnit(unit);
        setIsUnitDropdownOpen(false);
        setUnitSearchQuery('');
        setUnits([]);
    };

    const handleProceed = () => {
        if (selectedUnit && customer) {
            // Navigate to AddCustomerToUnit with the selected unit and customer data
            navigate(`/add-customer/${selectedUnit._id}?customerId=${customerId}&customerName=${encodeURIComponent(customer.name)}&unitPrice=${selectedUnit.price}&unitLabel=${encodeURIComponent(selectedUnit.label)}&customerSwagId=${encodeURIComponent(customer.swag_id || '')}`);
        }
    };

    if (customerLoading) {
        return (
            <div className={styles.container}>
                <div className={styles.loadingState}>
                    <h3>Loading customer details...</h3>
                </div>
            </div>
        );
    }

    if (!customer) {
        return (
            <div className={styles.container}>
                <div className={styles.errorState}>
                    <h3>Customer not found</h3>
                    <p>The customer could not be loaded.</p>
                    <button className={styles.backButton} onClick={onCancel}>
                        Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <button className={styles.backButton} onClick={onCancel}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M15 18L9 12L15 6" />
                        </svg>
                        Back
                    </button>
                    <h1 className={styles.title}>Add Unit to Customer</h1>
                </div>
            </div>
            <div className={styles.content}>

                <div className={styles.customerInfo}>
                    <div className={styles.customerCard}>
                        <div className={styles.customerAvatar}>
                            {customer.name.charAt(0).toUpperCase()}
                        </div>
                        <div className={styles.customerDetails}>
                            <h3 className={styles.customerName}>{customer.name}</h3>
                            <p className={styles.customerEmail}>{customer.email}</p>
                            {customer.swag_id && (
                                <p className={styles.customerSwagId}>SWAG ID: {customer.swag_id}</p>
                            )}
                            {customer.address && (
                                <p className={styles.customerAddress}>{customer.address}</p>
                            )}
                        </div>
                    </div>
                </div>

                <div className={styles.selectionSection}>
                    <div className={styles.projectSection}>
                        <label className={styles.sectionLabel}>1. Select Project</label>
                        <div className={styles.dropdownContainer} ref={projectDropdownRef}>
                            <div
                                className={styles.dropdownTrigger}
                                onClick={() => setIsProjectDropdownOpen(!isProjectDropdownOpen)}
                            >
                                <div className={`${styles.selectedValue} ${!selectedProject ? styles.empty : ''}`}>
                                    {selectedProject ? (
                                        <div className={styles.projectInfo}>
                                            <span className={styles.projectName}>{selectedProject.name}</span>
                                            <span className={styles.projectLocation}>{selectedProject.location}</span>
                                        </div>
                                    ) : (
                                        'Select project...'
                                    )}
                                </div>
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    style={{
                                        transform: isProjectDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.3s ease'
                                    }}
                                >
                                    <path d="m6 9 6 6 6-6" />
                                </svg>
                            </div>

                            {isProjectDropdownOpen && (
                                <div className={styles.dropdownMenu}>
                                    <div className={styles.searchContainer}>
                                        <input
                                            type="text"
                                            placeholder="Search projects..."
                                            value={projectSearchQuery}
                                            onChange={(e) => setProjectSearchQuery(e.target.value)}
                                            className={styles.searchInput}
                                            autoFocus
                                        />
                                    </div>
                                    <div className={styles.optionsList}>
                                        {projectsLoading ? (
                                            <div className={styles.loadingMessage}>
                                                Searching...
                                            </div>
                                        ) : projects.length > 0 ? (
                                            projects.map(project => (
                                                <div
                                                    key={project._id}
                                                    className={styles.option}
                                                    onClick={() => handleProjectSelect(project)}
                                                >
                                                    <div className={styles.projectInfo}>
                                                        <div className={styles.projectName}>
                                                            {project.name}
                                                        </div>
                                                        <div className={styles.projectLocation}>
                                                            {project.location}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : projectSearchQuery.trim().length > 0 ? (
                                            <div className={styles.noResults}>
                                                No projects found
                                            </div>
                                        ) : (
                                            <div className={styles.noResults}>
                                                Start typing to search projects
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {selectedProject && (
                        <div className={styles.unitSection}>
                            <label className={styles.sectionLabel}>2. Select Unit</label>
                            <div className={styles.dropdownContainer} ref={unitDropdownRef}>
                                <div
                                    className={styles.dropdownTrigger}
                                    onClick={() => setIsUnitDropdownOpen(!isUnitDropdownOpen)}
                                >
                                    <div className={`${styles.selectedValue} ${!selectedUnit ? styles.empty : ''}`}>
                                        {selectedUnit ? (
                                            <div className={styles.unitInfo}>
                                                <span className={styles.unitLabel}>{selectedUnit.label}</span>
                                                <span className={styles.unitPrice}>${selectedUnit.price.toLocaleString()}</span>
                                            </div>
                                        ) : (
                                            'Select unit...'
                                        )}
                                    </div>
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        style={{
                                            transform: isUnitDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                            transition: 'transform 0.3s ease'
                                        }}
                                    >
                                        <path d="m6 9 6 6 6-6" />
                                    </svg>
                                </div>

                                {isUnitDropdownOpen && (
                                    <div className={styles.dropdownMenu}>
                                        <div className={styles.searchContainer}>
                                            <input
                                                type="text"
                                                placeholder="Search units..."
                                                value={unitSearchQuery}
                                                onChange={(e) => setUnitSearchQuery(e.target.value)}
                                                className={styles.searchInput}
                                                autoFocus
                                            />
                                        </div>
                                        <div className={styles.optionsList}>
                                            {unitsLoading ? (
                                                <div className={styles.loadingMessage}>
                                                    Searching...
                                                </div>
                                            ) : units.length > 0 ? (
                                                units.map(unit => (
                                                    <div
                                                        key={unit._id}
                                                        className={styles.option}
                                                        onClick={() => handleUnitSelect(unit)}
                                                    >
                                                        <div className={styles.unitInfo}>
                                                            <div className={styles.unitLabel}>
                                                                {unit.label}
                                                            </div>
                                                            <div className={styles.unitPrice}>
                                                                ${unit.price.toLocaleString()}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : unitSearchQuery.trim().length > 0 ? (
                                                <div className={styles.noResults}>
                                                    No units found
                                                </div>
                                            ) : (
                                                <div className={styles.noResults}>
                                                    Start typing to search units
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {selectedProject && selectedUnit && (
                    <div className={styles.summary}>
                        <h3 className={styles.summaryTitle}>Selection Summary</h3>
                        <div className={styles.summaryContent}>
                            <div className={styles.summaryItem}>
                                <span className={styles.summaryLabel}>Customer:</span>
                                <span className={styles.summaryValue}>{customer.name}</span>
                            </div>
                            <div className={styles.summaryItem}>
                                <span className={styles.summaryLabel}>Project:</span>
                                <span className={styles.summaryValue}>{selectedProject.name}</span>
                            </div>
                            <div className={styles.summaryItem}>
                                <span className={styles.summaryLabel}>Unit:</span>
                                <span className={styles.summaryValue}>{selectedUnit.label}</span>
                            </div>
                            <div className={styles.summaryItem}>
                                <span className={styles.summaryLabel}>Price:</span>
                                <span className={styles.summaryValue}>${selectedUnit.price.toLocaleString()}</span>
                            </div>
                        </div>
                    </div>
                )}

                <div className={styles.actions}>
                    {selectedProject && selectedUnit && (
                        <button
                            className={styles.proceedButton}
                            onClick={handleProceed}
                        >
                            Proceed to Assignment
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AddUnitToCustomer;
